import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Paper,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Close,
  DragHandle,
  ShortText,
  Subject,
  CheckCircleOutline,
  CheckBox,
  ArrowDropDown,
  CalendarToday,
  Timer,
  Save,
  Phone,
  Email,
  Numbers,
  Schedule,
  WarningAmber,
  Visibility,
  Lock,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import providerFormService from '../../services/providerFormService';
import { trackProviderSectionSave } from '../../services/analyticsService';
import DynamicFormRenderer from '../Forms/DynamicFormRenderer';
import DialogHeader from '../Common/DialogHeader';

const FIELD_TYPES = {
  SHORT_TEXT: 'SHORT_TEXT',
  MULTILINE_TEXT: 'MULTILINE_TEXT',
  YES_NO: 'YES_NO',
  CHECKBOXES: 'CHECKBOXES',
  DROPDOWN: 'DROPDOWN',
  DATE: 'DATE',
  DURATION: 'DURATION',
  PHONE: 'PHONE',
  NUMBER: 'NUMBER',
  EMAIL: 'EMAIL',
  TIME: 'TIME',
};

const FIELD_TYPE_LABELS = {
  SHORT_TEXT: 'Rövid szöveg',
  MULTILINE_TEXT: 'Hosszú szöveg',
  YES_NO: 'Igen / Nem',
  CHECKBOXES: 'Jelölőnégyzetek',
  DROPDOWN: 'Legördülő lista',
  DATE: 'Dátum',
  DURATION: 'Időtartam',
  PHONE: 'Telefonszám',
  NUMBER: 'Szám',
  EMAIL: 'E-mail cím',
  TIME: 'Időpont',
};

const FIELD_ICONS = {
  SHORT_TEXT: <ShortText />,
  MULTILINE_TEXT: <Subject />,
  YES_NO: <CheckCircleOutline />,
  CHECKBOXES: <CheckBox />,
  DROPDOWN: <ArrowDropDown />,
  DATE: <CalendarToday />,
  DURATION: <Timer />,
  PHONE: <Phone />,
  NUMBER: <Numbers />,
  EMAIL: <Email />,
  TIME: <Schedule />,
};

// Opcionálisan bevonható rendszermezők (palettán jelennek meg)
const OPTIONAL_SYSTEM_FIELDS = [
  {
    systemKey: 'eventType',
    type: 'SHORT_TEXT',
    label: 'Esemény típusa',
    placeholder: 'Pl. esküvő, születésnap...',
    isRequired: false,
  },
  {
    systemKey: 'durationHours',
    type: 'NUMBER',
    label: 'Esemény időtartama (óra)',
    placeholder: 'Pl. 4',
    isRequired: false,
  },
];

const SYSTEM_KEY_LABELS = {
  eventDate: 'Esemény dátuma',
  eventTime: 'Időpont',
  guestCount: 'Vendégek száma',
  contactName: 'Kapcsolattartó neve',
  contactEmail: 'E-mail cím',
  contactPhone: 'Telefonszám',
  eventAddress: 'Helyszín (cím)',
  eventCity: 'Város',
  eventType: 'Esemény típusa',
  durationHours: 'Időtartam',
};

// Sortable field sor komponens
const SortableFieldRow = ({ field, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isSystemField = !!field.systemKey;

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        px: { xs: 0.5, sm: 1.5 },
        py: 0.5,
        gap: { xs: 0.5, sm: 1 },
        borderColor: isSystemField ? 'primary.light' : 'divider',
        bgcolor: isSystemField ? 'rgba(42, 93, 50, 0.04)' : 'background.paper',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      {/* touchAction: none csak a fogantyún — így érintéssel húzható, a sor többi része görget */}
      <IconButton
        size="small"
        aria-label="Mező áthelyezése"
        {...listeners}
        {...attributes}
        sx={{ cursor: 'grab', color: 'text.secondary', flexShrink: 0, width: 40, height: 40, touchAction: 'none' }}
      >
        <DragHandle fontSize="small" />
      </IconButton>

      <Box sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
        {FIELD_ICONS[field.type] || <ShortText />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* A csillag a csonkolt címkén kívül marad, hogy hosszú címkénél is látszódjon */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ minWidth: 0 }}>
            {field.label}
          </Typography>
          {field.isRequired && (
            <Typography component="span" variant="body2" color="error" sx={{ ml: 0.5, flexShrink: 0 }} aria-label="kötelező">*</Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap>
          {FIELD_TYPE_LABELS[field.type] || field.type}
          {isSystemField && (
            <Chip
              label="Rendszermező"
              size="small"
              icon={<Lock sx={{ fontSize: '10px !important' }} />}
              sx={{ ml: 1, height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
            />
          )}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <Tooltip title="Szerkesztés">
          <IconButton size="small" aria-label="Szerkesztés" onClick={() => onEdit(field)} sx={{ width: 40, height: 40 }}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Törlés">
          <IconButton size="small" aria-label="Törlés" onClick={() => onDelete(field)} color="error" sx={{ width: 40, height: 40 }}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

const ProviderFormEditor = ({ providerId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Telefonon a dialógusok teljes képernyősek (a billentyűzet mellett is kényelmesen kitölthetők)
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mobileTab, setMobileTab] = useState(0);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Mező szerkesztő dialog
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldPlaceholder, setFieldPlaceholder] = useState('');
  const [fieldIsRequired, setFieldIsRequired] = useState(false);
  const [fieldType, setFieldType] = useState('SHORT_TEXT');
  const [fieldOptions, setFieldOptions] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Törlés megerősítő dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);

  // Form beállítások dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadForm = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await providerFormService.ensureDefaultForm(providerId);
      if (response.success) {
        const sorted = { ...response.data, fields: [...response.data.fields].sort((a, b) => a.order - b.order) };
        setForm(sorted);
      } else {
        setError(response.message || 'A form betöltése sikertelen.');
      }
    } catch (err) {
      setError('Hiba történt a form betöltése során.');
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    if (providerId) loadForm();
  }, [providerId, loadForm]);

  // Meglévő systemKey-ek a formban
  const existingSystemKeys = (form?.fields || []).map(f => f.systemKey).filter(Boolean);

  // Palettán megjelenő opcionális mezők (amik még nincsenek a formban)
  const availablePaletteFields = OPTIONAL_SYSTEM_FIELDS.filter(
    f => !existingSystemKeys.includes(f.systemKey)
  );

  // --- Mező szerkesztés ---
  const openEditDialog = (field) => {
    setEditingField(field);
    setFieldLabel(field.label);
    setFieldPlaceholder(field.placeholder || '');
    setFieldIsRequired(field.isRequired);
    setFieldType(field.type);
    setFieldOptions((field.options || []).join(', '));
    setFieldErrors({});
    setFieldDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingField(null);
    setFieldLabel('');
    setFieldPlaceholder('');
    setFieldIsRequired(false);
    setFieldType('SHORT_TEXT');
    setFieldOptions('');
    setFieldErrors({});
    setFieldDialogOpen(true);
  };

  const validateFieldDialog = () => {
    const errs = {};
    if (!fieldLabel.trim()) errs.label = 'A felirat kötelező.';
    if (['CHECKBOXES', 'DROPDOWN'].includes(fieldType) && !fieldOptions.trim()) {
      errs.options = 'Az opciók kötelezőek ehhez a típushoz (vesszővel elválasztva).';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSaveField = async () => {
    if (!validateFieldDialog()) return;
    setActionLoading(true);
    try {
      const fieldData = {
        type: fieldType,
        label: fieldLabel.trim(),
        placeholder: fieldPlaceholder.trim() || null,
        isRequired: fieldIsRequired,
        options: ['CHECKBOXES', 'DROPDOWN'].includes(fieldType)
          ? fieldOptions.split(',').map(o => o.trim()).filter(Boolean)
          : [],
        order: editingField
          ? editingField.order
          : (form.fields.length > 0 ? Math.max(...form.fields.map(f => f.order)) + 1 : 0),
      };

      let response;
      if (editingField) {
        response = await providerFormService.updateFormField(form.id, editingField.id, fieldData);
      } else {
        response = await providerFormService.addFormField(form.id, fieldData);
      }

      if (response.success) {
        showSnackbar(editingField ? 'Mező frissítve.' : 'Mező hozzáadva.');
        trackProviderSectionSave(providerId, 'form_szerkeszto');
        setFieldDialogOpen(false);
        loadForm();
      } else {
        showSnackbar(response.message || 'Hiba a mező mentésekor.', 'error');
      }
    } catch (err) {
      showSnackbar('Szerver hiba.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Törlés ---
  const requestDelete = (field) => {
    setFieldToDelete(field);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!fieldToDelete) return;
    setActionLoading(true);
    try {
      const response = await providerFormService.deleteFormField(form.id, fieldToDelete.id);
      if (response.success) {
        showSnackbar('Mező törölve.');
        setDeleteDialogOpen(false);
        setFieldToDelete(null);
        loadForm();
      } else {
        showSnackbar(response.message || 'Hiba a törlés során.', 'error');
      }
    } catch (err) {
      showSnackbar('Szerver hiba.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Paletta mező hozzáadása ---
  const addPaletteField = async (paletteField) => {
    setActionLoading(true);
    try {
      const order = form.fields.length > 0 ? Math.max(...form.fields.map(f => f.order)) + 1 : 0;
      const response = await providerFormService.addFormField(form.id, {
        type: paletteField.type,
        label: paletteField.label,
        placeholder: paletteField.placeholder || null,
        isRequired: paletteField.isRequired,
        options: [],
        order,
        systemKey: paletteField.systemKey,
      });
      if (response.success) {
        showSnackbar(`"${paletteField.label}" mező hozzáadva.`);
        loadForm();
      } else {
        showSnackbar(response.message || 'Hiba a hozzáadás során.', 'error');
      }
    } catch (err) {
      showSnackbar('Szerver hiba.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Drag & Drop sorrend ---
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = form.fields.findIndex(f => f.id === active.id);
    const newIndex = form.fields.findIndex(f => f.id === over.id);
    if (oldIndex === newIndex) return;

    const reordered = arrayMove(form.fields, oldIndex, newIndex).map((f, i) => ({ ...f, order: i }));
    setForm(prev => ({ ...prev, fields: reordered }));

    setActionLoading(true);
    try {
      // Egy kérés, egy tranzakció — nem maradhat félig átrendezett állapot
      const response = await providerFormService.reorderFormFields(form.id, reordered.map(f => f.id));
      if (!response.success) throw new Error(response.message);
      showSnackbar('Sorrend mentve.');
    } catch (err) {
      showSnackbar('Hiba a sorrend mentésekor.', 'error');
      loadForm();
    } finally {
      setActionLoading(false);
    }
  };

  // --- Form beállítások ---
  const openSettings = () => {
    setFormTitle(form?.title || '');
    setFormDescription(form?.description || '');
    setSettingsDialogOpen(true);
  };

  const handleSaveSettings = async () => {
    if (!formTitle.trim()) return;
    setActionLoading(true);
    try {
      const response = await providerFormService.updateProviderForm(form.id, {
        title: formTitle.trim(),
        description: formDescription.trim() || null,
        isActive: form.isActive,
      });
      if (response.success) {
        showSnackbar('Beállítások mentve.');
        trackProviderSectionSave(providerId, 'form_beallitasok');
        setSettingsDialogOpen(false);
        loadForm();
      } else {
        showSnackbar(response.message || 'Hiba a mentés során.', 'error');
      }
    } catch (err) {
      showSnackbar('Szerver hiba.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}>
        <CircularProgress size={24} />
        <Typography>Form betöltése...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" action={<Button onClick={loadForm}>Újra</Button>}>{error}</Alert>;
  }

  const editorPanel = (
    <Box>
      {/* Form fejléc */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontFamily: 'Playfair Display, serif', color: 'primary.dark' }}>
            {form?.title}
          </Typography>
          {form?.description && (
            <Typography variant="caption" color="text.secondary">{form.description}</Typography>
          )}
        </Box>
        <Tooltip title="Űrlap neve és leírása">
          <Button size="small" startIcon={<Edit fontSize="small" />} onClick={openSettings} sx={{ flexShrink: 0, minHeight: 40 }}>
            Átnevezés
          </Button>
        </Tooltip>
      </Box>

      {/* Paletta – opcionális rendszermezők */}
      {availablePaletteFields.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Opcionális mezők (kattintással hozzáadhatók)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {availablePaletteFields.map(pf => (
              <Chip
                key={pf.systemKey}
                label={pf.label}
                icon={FIELD_ICONS[pf.type]}
                onClick={() => addPaletteField(pf)}
                variant="outlined"
                size="small"
                disabled={actionLoading}
                sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' } }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Mezők listája */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={(form?.fields || []).map(f => f.id)} strategy={verticalListSortingStrategy}>
          {(form?.fields || []).map(field => (
            <SortableFieldRow
              key={field.id}
              field={field}
              onEdit={openEditDialog}
              onDelete={requestDelete}
            />
          ))}
        </SortableContext>
      </DndContext>

      {(form?.fields || []).length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>Nincsenek mezők az űrlapon.</Alert>
      )}

      {/* Új mező gomb */}
      <Button
        variant="outlined"
        startIcon={<Add />}
        onClick={openCreateDialog}
        disabled={actionLoading}
        fullWidth
        sx={{ mt: 1 }}
      >
        Egyéni mező hozzáadása
      </Button>
    </Box>
  );

  const previewPanel = (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Visibility fontSize="small" />
        Előnézet – így látják az ügyfelek
      </Typography>
      {form && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.default' }}>
          <DynamicFormRenderer form={form} showTitle={true} />
        </Paper>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Mobil: Tab váltás */}
      {isMobile ? (
        <Box>
          <Tabs
            value={mobileTab}
            onChange={(_, v) => setMobileTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Szerkesztő" />
            <Tab label="Előnézet" />
          </Tabs>
          {mobileTab === 0 ? editorPanel : previewPanel}
        </Box>
      ) : (
        /* Desktop: Split panel */
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, alignItems: 'start' }}>
          <Box>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Szerkesztő
            </Typography>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              {editorPanel}
            </Paper>
          </Box>
          <Box sx={{ position: 'sticky', top: 80 }}>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Élő előnézet
            </Typography>
            {previewPanel}
          </Box>
        </Box>
      )}

      {/* Mező szerkesztő Dialog */}
      <Dialog open={fieldDialogOpen} onClose={() => setFieldDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader
            icon={editingField ? <Edit /> : <Add />}
            title={editingField ? 'Mező szerkesztése' : 'Egyéni mező hozzáadása'}
            onClose={() => !actionLoading && setFieldDialogOpen(false)}
          />
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Típus</InputLabel>
            <Select
              value={fieldType}
              label="Típus"
              onChange={(e) => setFieldType(e.target.value)}
              disabled={!!editingField}
            >
              {Object.keys(FIELD_TYPES)
                // Rendszermezőnél a speciális típusok rejtve — kivéve a mező saját típusát, különben üres a mező
                .filter(t => t === fieldType || !['DATE', 'TIME', 'EMAIL', 'PHONE', 'NUMBER'].includes(t) || !editingField?.systemKey)
                .map(type => (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {FIELD_ICONS[type]}
                      {FIELD_TYPE_LABELS[type]}
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Kérdés / Felirat"
            value={fieldLabel}
            onChange={(e) => {
              setFieldLabel(e.target.value);
              if (fieldErrors.label && e.target.value.trim()) setFieldErrors(prev => ({ ...prev, label: undefined }));
            }}
            onBlur={() => !fieldLabel.trim() && setFieldErrors(prev => ({ ...prev, label: 'A felirat kötelező.' }))}
            error={!!fieldErrors.label}
            helperText={fieldErrors.label}
            sx={{ mb: 2 }}
            autoFocus
          />

          <TextField
            fullWidth
            label="Placeholder szöveg (opcionális)"
            value={fieldPlaceholder}
            onChange={(e) => setFieldPlaceholder(e.target.value)}
            helperText="Ha üresen hagyja, nem jelenik meg placeholder."
            sx={{ mb: 2 }}
          />

          {['CHECKBOXES', 'DROPDOWN'].includes(fieldType) && (
            <TextField
              fullWidth
              label="Opciók (vesszővel elválasztva)"
              value={fieldOptions}
              onChange={(e) => {
                setFieldOptions(e.target.value);
                if (fieldErrors.options && e.target.value.trim()) setFieldErrors(prev => ({ ...prev, options: undefined }));
              }}
              error={!!fieldErrors.options}
              helperText={fieldErrors.options || 'Pl. Lehetőség 1, Lehetőség 2, Lehetőség 3'}
              sx={{ mb: 2 }}
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={fieldIsRequired}
                onChange={(e) => setFieldIsRequired(e.target.checked)}
                color="primary"
              />
            }
            label="Kötelező mező"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setFieldDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveField}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Save />}
          >
            {editingField ? 'Mentés' : 'Hozzáadás'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Törlés megerősítő Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<Delete />} title="Mező törlése" />
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {fieldToDelete?.systemKey ? (
            <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
              Ez egy rendszermező (<strong>{SYSTEM_KEY_LABELS[fieldToDelete.systemKey] || fieldToDelete.systemKey}</strong>).
              Ha törli, az ügyfelek nem tudják megadni ezt az adatot az ajánlatkérő űrlapon.
            </Alert>
          ) : null}
          <Typography>
            Biztosan törli a <strong>„{fieldToDelete?.label}"</strong> mezőt?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
            Mégse
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Form beállítások Dialog */}
      <Dialog open={settingsDialogOpen} onClose={() => setSettingsDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isPhone}>
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<Edit />} title="Űrlap beállításai" onClose={() => !actionLoading && setSettingsDialogOpen(false)} />
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <TextField
            fullWidth
            label="Űrlap neve"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            sx={{ mb: 2 }}
            required
            error={!formTitle.trim()}
            helperText={!formTitle.trim() ? 'Az űrlap neve kötelező.' : undefined}
          />
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Leírás (opcionális)"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            helperText="Rövid bevezető szöveg az ajánlatkérő form tetején."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setSettingsDialogOpen(false)} disabled={actionLoading}>Mégse</Button>
          <Button
            variant="contained"
            onClick={handleSaveSettings}
            disabled={actionLoading || !formTitle.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Save />}
          >
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        // A profilszerkesztő minden fülén ugyanott: alul középen, a mentési sáv és a lebegő gomb fölött
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 96, sm: 104 } }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderFormEditor;
